import React, { useState, useEffect, useCallback } from 'react';
import OAuthConnectButton from './OAuthConnectButton';

interface CompetingUrl {
  url: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

interface CannibalizationRow {
  query: string;
  totalImpressions: number;
  totalClicks: number;
  competingUrls: CompetingUrl[];
}

interface AnalysisResult {
  totalQueries: number;
  cannibalizedCount: number;
  rows: CannibalizationRow[];
}

const DATE_RANGES = [
  { label: '3 months', value: '3m' },
  { label: '6 months', value: '6m' },
  { label: '12 months', value: '12m' },
];

const KeywordCannibalism: React.FC = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [properties, setProperties] = useState<string[]>([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [dateRange, setDateRange] = useState('3m');
  const [loading, setLoading] = useState(false);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const getAuthHeaders = useCallback((): Record<string, string> => {
    const token = localStorage.getItem('auth_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const fetchProperties = useCallback(async () => {
    setLoadingProperties(true);
    setError(null);
    try {
      const res = await fetch('/api/seotools/keyword-cannibalism/properties', {
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.detail || `Failed to load properties (${res.status})`);
      }
      const data = await res.json();
      const rawProps = Array.isArray(data) ? data : data.properties ?? [];
      // Backend returns [{url, permissionLevel}] — extract URLs as strings
      const props: string[] = rawProps.map((p: any) => typeof p === 'string' ? p : p.url || p);
      setProperties(props);
      if (props.length > 0 && !selectedProperty) {
        setSelectedProperty(props[0]);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load GSC properties');
    } finally {
      setLoadingProperties(false);
    }
  }, [getAuthHeaders, selectedProperty]);

  const handleConnected = useCallback(() => {
    setIsConnected(true);
  }, []);

  useEffect(() => {
    if (isConnected) {
      fetchProperties();
    }
  }, [isConnected, fetchProperties]);

  const handleAnalyze = async () => {
    if (!selectedProperty) return;

    setLoading(true);
    setError(null);
    setResult(null);
    setExpandedRows(new Set());

    try {
      const res = await fetch('/api/seotools/keyword-cannibalism/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ siteUrl: selectedProperty, dateRange }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.detail || `Analysis failed (${res.status})`);
      }
      const raw = await res.json();
      // Map backend response shape to frontend interface
      const data: AnalysisResult = {
        totalQueries: raw.totalQueriesAnalyzed ?? raw.totalQueries ?? 0,
        cannibalizedCount: raw.cannibalisedCount ?? raw.cannibalizedCount ?? 0,
        rows: (raw.results ?? raw.rows ?? []).map((r: any) => ({
          query: r.query,
          totalImpressions: r.totalImpressions,
          totalClicks: r.totalClicks,
          competingUrls: (r.urls ?? r.competingUrls ?? []).map((u: any) => ({
            url: u.url,
            clicks: u.clicks,
            impressions: u.impressions,
            ctr: u.ctr ?? (u.impressions > 0 ? u.clicks / u.impressions : 0),
            position: u.position ?? 0,
          })),
        })),
      };
      // Sort by number of competing URLs descending, then by impressions
      data.rows.sort(
        (a, b) =>
          b.competingUrls.length - a.competingUrls.length ||
          b.totalImpressions - a.totalImpressions
      );
      setResult(data);
    } catch (err: any) {
      setError(err.message || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (index: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const getSeverityClasses = (urlCount: number) => {
    if (urlCount >= 3) return { row: 'bg-red-50', badge: 'bg-red-100 text-red-800 border-red-200' };
    if (urlCount === 2) return { row: 'bg-yellow-50', badge: 'bg-yellow-100 text-yellow-800 border-yellow-200' };
    return { row: '', badge: 'bg-gray-100 text-gray-700 border-gray-200' };
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-800">Keyword Cannibalism</h2>
        <p className="text-sm text-gray-500 mt-1">
          Connect your Google Search Console to detect queries where multiple pages compete for the same keyword.
        </p>
      </div>

      {/* GSC Connection */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <OAuthConnectButton
          service="gsc"
          label="Google Search Console"
          onConnect={handleConnected}
          onDisconnect={() => setIsConnected(false)}
        />
      </div>

      {/* Controls — only shown when connected */}
      {isConnected && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            {/* Property selector */}
            <div>
              <label htmlFor="gsc-property" className="block text-sm font-medium text-gray-700 mb-1">
                GSC Property
              </label>
              {loadingProperties ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Loading properties...
                </div>
              ) : (
                <select
                  id="gsc-property"
                  value={selectedProperty}
                  onChange={(e) => setSelectedProperty(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  disabled={loading || properties.length === 0}
                >
                  {properties.length === 0 && (
                    <option value="">No properties found</option>
                  )}
                  {properties.map((prop) => (
                    <option key={prop} value={prop}>
                      {prop}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Date range selector */}
            <div>
              <label htmlFor="date-range" className="block text-sm font-medium text-gray-700 mb-1">
                Date Range
              </label>
              <select
                id="date-range"
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                disabled={loading}
              >
                {DATE_RANGES.map((dr) => (
                  <option key={dr.value} value={dr.value}>
                    {dr.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Analyze button */}
            <div>
              <button
                onClick={handleAnalyze}
                disabled={loading || !selectedProperty}
                className="w-full px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Analyzing...
                  </span>
                ) : (
                  'Analyze'
                )}
              </button>
            </div>
          </div>

          {loading && (
            <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3">
              This may take a while for large sites with many queries. Please be patient.
            </p>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="space-y-6">
          {/* Summary bar */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg shadow border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-gray-800">{result.totalQueries.toLocaleString()}</div>
              <div className="text-xs text-gray-500 mt-1">Total Queries Analyzed</div>
            </div>
            <div className="bg-white rounded-lg shadow border border-red-200 p-4 text-center">
              <div className="text-2xl font-bold text-red-700">{result.cannibalizedCount.toLocaleString()}</div>
              <div className="text-xs text-gray-500 mt-1">Cannibalized Queries</div>
            </div>
            <div className="bg-white rounded-lg shadow border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-gray-800">
                {result.totalQueries > 0
                  ? ((result.cannibalizedCount / result.totalQueries) * 100).toFixed(1)
                  : '0'}
                %
              </div>
              <div className="text-xs text-gray-500 mt-1">Cannibalization Rate</div>
            </div>
          </div>

          {/* Results table */}
          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
            <div className="px-5 py-3 bg-gray-50 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700">
                Cannibalized Keywords{' '}
                <span className="text-gray-400 font-normal">
                  ({result.rows.length} {result.rows.length === 1 ? 'query' : 'queries'})
                </span>
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-3 font-medium text-gray-600 w-8"></th>
                    <th className="px-4 py-3 font-medium text-gray-600">Query</th>
                    <th className="px-4 py-3 font-medium text-gray-600 text-right">Total Impressions</th>
                    <th className="px-4 py-3 font-medium text-gray-600 text-right">Total Clicks</th>
                    <th className="px-4 py-3 font-medium text-gray-600 text-center"># Competing URLs</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {result.rows.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                        No keyword cannibalization detected.
                      </td>
                    </tr>
                  )}
                  {result.rows.map((row, idx) => {
                    const isExpanded = expandedRows.has(idx);
                    const severity = getSeverityClasses(row.competingUrls.length);

                    return (
                      <React.Fragment key={idx}>
                        {/* Main row */}
                        <tr
                          className={`${severity.row} hover:bg-gray-100 transition-colors cursor-pointer`}
                          onClick={() => toggleRow(idx)}
                        >
                          <td className="px-4 py-3 text-gray-400">
                            <svg
                              className={`h-4 w-4 transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                          </td>
                          <td className="px-4 py-3 font-medium text-gray-900">{row.query}</td>
                          <td className="px-4 py-3 text-right text-gray-700">
                            {row.totalImpressions.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-700">
                            {row.totalClicks.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span
                              className={`inline-block px-2.5 py-0.5 text-xs font-semibold rounded-full border ${severity.badge}`}
                            >
                              {row.competingUrls.length}
                            </span>
                          </td>
                        </tr>

                        {/* Expanded detail rows */}
                        {isExpanded && (
                          <tr>
                            <td colSpan={5} className="px-0 py-0">
                              <div className="bg-gray-50 border-t border-b border-gray-200">
                                <table className="min-w-full text-sm">
                                  <thead>
                                    <tr className="text-left">
                                      <th className="pl-14 pr-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Competing URL
                                      </th>
                                      <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider text-right">
                                        Clicks
                                      </th>
                                      <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider text-right">
                                        Impressions
                                      </th>
                                      <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider text-right">
                                        CTR
                                      </th>
                                      <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider text-right">
                                        Avg Position
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {row.competingUrls.map((cu, cuIdx) => (
                                      <tr key={cuIdx} className="hover:bg-gray-100 transition-colors">
                                        <td className="pl-14 pr-4 py-2 text-gray-700 max-w-[400px] truncate" title={cu.url}>
                                          <a
                                            href={cu.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:underline"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            {cu.url.replace(/^https?:\/\//, '')}
                                          </a>
                                        </td>
                                        <td className="px-4 py-2 text-right text-gray-700 font-medium">
                                          {cu.clicks.toLocaleString()}
                                        </td>
                                        <td className="px-4 py-2 text-right text-gray-600">
                                          {cu.impressions.toLocaleString()}
                                        </td>
                                        <td className="px-4 py-2 text-right text-gray-600">
                                          {(cu.ctr * 100).toFixed(1)}%
                                        </td>
                                        <td className="px-4 py-2 text-right text-gray-600">
                                          {cu.position.toFixed(1)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KeywordCannibalism;
